import { useRef, useState } from "react";
import {
  motion,
  useScroll,
  useSpring,
  useMotionValue,
  useTransform,
  useReducedMotion,
} from "framer-motion";
import Reveal from "./Reveal";
import { Compass, Search, PenLine, Clapperboard, Image, TrendingUp } from "lucide-react";

const SERVICES = [
  { id: "strategy", title: "Strategy", desc: "Figuring out what your content needs to accomplish and how to get there — with SEO, competitor research, and weekly strategy calls.", Icon: Compass },
  { id: "ideas", title: "Ideas & Research", desc: "Finding topics worth making instead of posting just for the sake of posting.", Icon: Search },
  { id: "scripting", title: "Scripting", desc: "Turning good ideas into videos people want to keep watching.", Icon: PenLine },
  { id: "editing", title: "Editing", desc: "Making the video clear, engaging, and worth watching.", Icon: Clapperboard },
  { id: "thumbnails", title: "Thumbnails & Titles", desc: "Making the value of the video clear before someone clicks.", Icon: Image },
  { id: "testing", title: "Testing & Improvement", desc: "Looking at what worked, what didn't, and what we can do better next time.", Icon: TrendingUp },
];

const MAP_PATH =
  "M50 0 C 85 70, 15 150, 50 230 S 85 380, 50 460 S 15 610, 50 690 S 85 860, 50 940 L 50 1000";

const StageCard = ({ s, i, active, dimmed, onActivate, onDeactivate }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 16 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), { stiffness: 120, damping: 16 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => {
        mx.set(0);
        my.set(0);
      }}
      style={{ perspective: 900 }}
    >
      <motion.div
        role="button"
        tabIndex={0}
        onHoverStart={onActivate}
        onHoverEnd={onDeactivate}
        onFocus={onActivate}
        onBlur={onDeactivate}
        style={reduce ? {} : { rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        animate={{
          opacity: dimmed ? 0.3 : 1,
          scale: active ? 1.03 : dimmed ? 0.985 : 1,
          filter: dimmed ? "blur(1.5px)" : "blur(0px)",
        }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className={`relative rounded-[30px] border bg-white p-8 sm:p-10 outline-none cursor-default transition-[border-color,box-shadow] duration-500 ${
          active
            ? "border-black/25 shadow-[0_44px_90px_rgba(0,0,0,0.16)]"
            : "border-black/[0.08] shadow-[0_10px_30px_rgba(0,0,0,0.04)]"
        }`}
        data-testid={`service-card-${s.id}`}
      >
        <div className="flex items-start justify-between gap-6">
          <span
            className={`font-display text-6xl sm:text-7xl font-semibold tracking-[-0.04em] leading-none transition-colors duration-500 ${
              active ? "text-[#0a0a0a]" : "text-black/[0.10]"
            }`}
            style={reduce ? {} : { transform: "translateZ(34px)" }}
          >
            {String(i + 1).padStart(2, "0")}
          </span>
          <motion.span
            animate={active ? { rotate: [0, -10, 8, 0], scale: 1.18 } : { rotate: 0, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full border transition-colors duration-500 ${
              active ? "border-[#0a0a0a] bg-[#0a0a0a] text-white" : "border-black/15 text-[#86868b]"
            }`}
            style={reduce ? {} : { transform: "translateZ(40px)" }}
          >
            <s.Icon className="h-5 w-5" strokeWidth={1.25} />
          </motion.span>
        </div>
        <div className="mt-10" style={reduce ? {} : { transform: "translateZ(22px)" }}>
          <h3 className="font-display text-2xl sm:text-[1.7rem] font-semibold tracking-[-0.02em] text-[#1d1d1f]">
            {s.title}
          </h3>
          <p className="mt-3 text-[15px] text-[#6e6e73] leading-relaxed max-w-sm">{s.desc}</p>
          <motion.div
            animate={{ opacity: active ? 1 : 0, x: active ? 0 : -10 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 flex items-center gap-3"
            aria-hidden="true"
          >
            <span className="h-px w-10 bg-[#0a0a0a]" />
            <span className="h-1.5 w-1.5 rotate-45 bg-[#0a0a0a]" />
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default function Services() {
  const [active, setActive] = useState(null);
  const pipelineRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: pipelineRef,
    offset: ["start 0.8", "end 0.55"],
  });
  const spineScale = useSpring(scrollYProgress, { stiffness: 55, damping: 20 });

  return (
    <section id="services" className="py-28 sm:py-40 bg-[#f5f5f7] overflow-hidden" data-testid="chapter-03-services-grid">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-20 sm:mb-28">
          <div className="lg:col-span-7">
            <Reveal>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.03em] leading-[1.06] text-[#1d1d1f]">
                VONTRAIL hand-held path to success.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-5">
            <p className="text-[#6e6e73] text-lg sm:text-xl leading-relaxed lg:text-right">
              One group handling everything instead of hiring five different people and paying them
              $10k+/mo to get 0 results.
            </p>
          </Reveal>
        </div>

        <div ref={pipelineRef} className="relative max-w-5xl mx-auto">
          <div className="absolute left-5 top-2 bottom-2 w-px bg-black/10 md:hidden" aria-hidden="true">
            <motion.div className="h-full w-full origin-top bg-[#0a0a0a]" style={{ scaleY: spineScale }} />
          </div>
          <svg
            className="absolute left-1/2 top-0 hidden h-full w-28 -translate-x-1/2 md:block"
            viewBox="0 0 100 1000"
            preserveAspectRatio="none"
            fill="none"
            aria-hidden="true"
          >
            <path
              d={MAP_PATH}
              stroke="rgba(0,0,0,0.14)"
              strokeWidth="2"
              strokeDasharray="6 8"
              vectorEffect="non-scaling-stroke"
              className="animate-march"
            />
            <motion.path
              d={MAP_PATH}
              stroke="#0a0a0a"
              strokeWidth="2.5"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
              style={{ pathLength: spineScale }}
            />
          </svg>

          <div className="space-y-10 md:space-y-16">
            {SERVICES.map((s, i) => {
              const flip = i % 2 === 1;
              const isActive = active === i;
              const dimmed = active !== null && active !== i;
              return (
                <Reveal key={s.id} delay={0.05} y={44}>
                  <div className={`relative md:flex ${flip ? "md:justify-start" : "md:justify-end"}`}>
                    <div
                      className="absolute left-5 md:left-1/2 top-10 -translate-x-1/2 z-10"
                      aria-hidden="true"
                    >
                      <motion.span
                        animate={{
                          scale: isActive ? 1.18 : 1,
                          boxShadow: isActive
                            ? "0 0 0 7px rgba(10,10,10,0.10), 0 0 34px rgba(0,0,0,0.45)"
                            : "0 0 0 0 rgba(10,10,10,0), 0 0 14px rgba(0,0,0,0.18)",
                        }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-[#f5f5f7] bg-[#0a0a0a] text-[11px] font-semibold tracking-wide text-white"
                      >
                        {String(i + 1).padStart(2, "0")}
                      </motion.span>
                      {isActive && (
                        <motion.span
                          initial={{ scale: 0.7, opacity: 0.5 }}
                          animate={{ scale: 2, opacity: 0 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "easeOut" }}
                          className="absolute inset-0 rounded-full border border-[#0a0a0a]"
                        />
                      )}
                    </div>
                    <div
                      className={`hidden md:block absolute top-14 h-px w-14 bg-black/15 ${
                        flip ? "right-1/2 mr-4" : "left-1/2 ml-4"
                      }`}
                      aria-hidden="true"
                    />
                    <div className="pl-14 md:pl-0 md:w-[calc(50%-3.5rem)]">
                      <StageCard
                        s={s}
                        i={i}
                        active={isActive}
                        dimmed={dimmed}
                        onActivate={() => setActive(i)}
                        onDeactivate={() => setActive(null)}
                      />
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
